export type SupportRequestStatus = "open" | "resolved";
export type SupportAccessGrantStatus =
  | "requested"
  | "approved"
  | "declined"
  | "revoked"
  | "expired";
export type SupportMessageAuthorKind = "user" | "admin";
export type SupportAccessGrantDecision = "approve" | "decline";

export const SUPPORT_SUBJECT_MAX_LENGTH = 200;
export const SUPPORT_MESSAGE_MAX_LENGTH = 4_000;
export const SUPPORT_NOTE_MAX_LENGTH = 1_000;
export const SUPPORT_GRANT_DURATION_OPTIONS = [1, 3, 7, 14, 30] as const;

export interface SupportWorkspace {
  targetType: string;
  targetID: string;
  role: string;
  displayName: string;
  sportID: string | null;
  leagueID: string | null;
  organizationID: string | null;
}

export interface SupportMessage {
  id: string;
  authorKind: SupportMessageAuthorKind;
  authorUserID: string;
  authorName: string;
  body: string;
  createdAt: Date;
}

export interface SupportRequest {
  id: string;
  userID: string;
  name: string;
  email: string;
  subject: string;
  status: SupportRequestStatus;
  workspace: SupportWorkspace | null;
  messages: SupportMessage[];
  createdAt: Date;
  updatedAt: Date;
  resolvedAt: Date | null;
  resolvedBy: string | null;
}

export interface SupportAccessGrant {
  id: string;
  supportRequestID: string;
  requestingAdminUserID: string;
  adminName: string;
  adminEmail: string;
  subjectUserID: string;
  workspace: SupportWorkspace;
  durationDays: number;
  note: string | null;
  status: SupportAccessGrantStatus;
  requestedAt: Date;
  decidedAt: Date | null;
  approvedAt: Date | null;
  expiresAt: Date | null;
  revokedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface SupportInbox {
  requests: SupportRequest[];
  grants: SupportAccessGrant[];
}

export interface AdminSupportRequestDetail {
  request: SupportRequest;
  grants: SupportAccessGrant[];
}

export class SupportResponseDecodeError extends Error {
  readonly fieldPath: string;

  constructor(fieldPath: string, expected: string) {
    super(`Invalid support response at ${fieldPath}: expected ${expected}.`);
    this.name = "SupportResponseDecodeError";
    this.fieldPath = fieldPath;
  }
}

const SUPPORT_REQUEST_STATUSES: readonly SupportRequestStatus[] = ["open", "resolved"];
const SUPPORT_GRANT_STATUSES: readonly SupportAccessGrantStatus[] = [
  "requested",
  "approved",
  "declined",
  "revoked",
  "expired",
];
const SUPPORT_AUTHOR_KINDS: readonly SupportMessageAuthorKind[] = ["user", "admin"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) throw new SupportResponseDecodeError(path, "an object");
  return value;
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string") throw new SupportResponseDecodeError(path, "a string");
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  if (value === null) return null;
  return string(value, path);
}

function enumValue<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
): T {
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    throw new SupportResponseDecodeError(path, allowed.map((candidate) => `"${candidate}"`).join(" or "));
  }
  return value as T;
}

/**
 * Functions serialize Firestore timestamps with `Date#toISOString`, so accept
 * that exact UTC wire shape rather than JavaScript's permissive date grammar.
 */
export function decodeSupportDate(value: unknown, path = "date"): Date {
  if (
    typeof value !== "string"
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    throw new SupportResponseDecodeError(path, "an ISO-8601 UTC timestamp");
  }
  const decoded = new Date(value);
  if (Number.isNaN(decoded.getTime()) || decoded.toISOString() !== value) {
    throw new SupportResponseDecodeError(path, "a valid ISO-8601 UTC timestamp");
  }
  return decoded;
}

function nullableDate(value: unknown, path: string): Date | null {
  if (value === null) return null;
  return decodeSupportDate(value, path);
}

export function decodeSupportWorkspace(value: unknown, path = "workspace"): SupportWorkspace {
  const data = record(value, path);
  return {
    targetType: string(data.targetType, `${path}.targetType`),
    targetID: string(data.targetID, `${path}.targetID`),
    role: string(data.role, `${path}.role`),
    displayName: string(data.displayName, `${path}.displayName`),
    sportID: nullableString(data.sportID, `${path}.sportID`),
    leagueID: nullableString(data.leagueID, `${path}.leagueID`),
    organizationID: nullableString(data.organizationID, `${path}.organizationID`),
  };
}

export function decodeSupportMessage(value: unknown, path = "message"): SupportMessage {
  const data = record(value, path);
  return {
    id: string(data.id, `${path}.id`),
    authorKind: enumValue(data.authorKind, SUPPORT_AUTHOR_KINDS, `${path}.authorKind`),
    authorUserID: string(data.authorUserID, `${path}.authorUserID`),
    authorName: string(data.authorName, `${path}.authorName`),
    body: string(data.body, `${path}.body`),
    createdAt: decodeSupportDate(data.createdAt, `${path}.createdAt`),
  };
}

export function decodeSupportRequest(value: unknown, path = "request"): SupportRequest {
  const data = record(value, path);
  if (!Array.isArray(data.messages)) {
    throw new SupportResponseDecodeError(`${path}.messages`, "an array");
  }
  return {
    id: string(data.id, `${path}.id`),
    userID: string(data.userID, `${path}.userID`),
    name: string(data.name, `${path}.name`),
    email: string(data.email, `${path}.email`),
    subject: string(data.subject, `${path}.subject`),
    status: enumValue(data.status, SUPPORT_REQUEST_STATUSES, `${path}.status`),
    workspace: data.workspace === null
      ? null
      : decodeSupportWorkspace(data.workspace, `${path}.workspace`),
    messages: data.messages.map((message, index) =>
      decodeSupportMessage(message, `${path}.messages[${index}]`)),
    createdAt: decodeSupportDate(data.createdAt, `${path}.createdAt`),
    updatedAt: decodeSupportDate(data.updatedAt, `${path}.updatedAt`),
    resolvedAt: nullableDate(data.resolvedAt, `${path}.resolvedAt`),
    resolvedBy: nullableString(data.resolvedBy, `${path}.resolvedBy`),
  };
}

export function decodeSupportAccessGrant(value: unknown, path = "grant"): SupportAccessGrant {
  const data = record(value, path);
  if (
    typeof data.durationDays !== "number"
    || !Number.isInteger(data.durationDays)
    || !SUPPORT_GRANT_DURATION_OPTIONS.includes(
      data.durationDays as (typeof SUPPORT_GRANT_DURATION_OPTIONS)[number],
    )
  ) {
    throw new SupportResponseDecodeError(
      `${path}.durationDays`,
      `one of ${SUPPORT_GRANT_DURATION_OPTIONS.join(", ")}`,
    );
  }
  return {
    id: string(data.id, `${path}.id`),
    supportRequestID: string(data.supportRequestID, `${path}.supportRequestID`),
    requestingAdminUserID: string(data.requestingAdminUserID, `${path}.requestingAdminUserID`),
    adminName: string(data.adminName, `${path}.adminName`),
    adminEmail: string(data.adminEmail, `${path}.adminEmail`),
    subjectUserID: string(data.subjectUserID, `${path}.subjectUserID`),
    workspace: decodeSupportWorkspace(data.workspace, `${path}.workspace`),
    durationDays: data.durationDays,
    note: nullableString(data.note, `${path}.note`),
    status: enumValue(data.status, SUPPORT_GRANT_STATUSES, `${path}.status`),
    requestedAt: decodeSupportDate(data.requestedAt, `${path}.requestedAt`),
    decidedAt: nullableDate(data.decidedAt, `${path}.decidedAt`),
    approvedAt: nullableDate(data.approvedAt, `${path}.approvedAt`),
    expiresAt: nullableDate(data.expiresAt, `${path}.expiresAt`),
    revokedAt: nullableDate(data.revokedAt, `${path}.revokedAt`),
    createdAt: decodeSupportDate(data.createdAt, `${path}.createdAt`),
    updatedAt: decodeSupportDate(data.updatedAt, `${path}.updatedAt`),
  };
}

function decodeRequestArray(value: unknown, path: string): SupportRequest[] {
  if (!Array.isArray(value)) throw new SupportResponseDecodeError(path, "an array");
  return value.map((request, index) => decodeSupportRequest(request, `${path}[${index}]`));
}

function decodeGrantArray(value: unknown, path: string): SupportAccessGrant[] {
  if (!Array.isArray(value)) throw new SupportResponseDecodeError(path, "an array");
  return value.map((grant, index) => decodeSupportAccessGrant(grant, `${path}[${index}]`));
}

export function decodeSupportRequestEnvelope(value: unknown): SupportRequest {
  const data = record(value, "response");
  return decodeSupportRequest(data.request, "response.request");
}

export function decodeSupportGrantEnvelope(value: unknown): SupportAccessGrant {
  const data = record(value, "response");
  return decodeSupportAccessGrant(data.grant, "response.grant");
}

export function decodeSupportRequestListEnvelope(value: unknown): SupportRequest[] {
  const data = record(value, "response");
  return decodeRequestArray(data.requests, "response.requests");
}

export function decodeSupportInbox(value: unknown): SupportInbox {
  const data = record(value, "response");
  return {
    requests: decodeRequestArray(data.requests, "response.requests"),
    grants: decodeGrantArray(data.grants, "response.grants"),
  };
}

export function decodeAdminSupportRequestDetail(value: unknown): AdminSupportRequestDetail {
  const data = record(value, "response");
  return {
    request: decodeSupportRequest(data.request, "response.request"),
    grants: decodeGrantArray(data.grants, "response.grants"),
  };
}

function newestUpdatedFirst<T extends { updatedAt: Date }>(values: T[]): T[] {
  return [...values].sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime());
}

export function openSupportRequests(requests: SupportRequest[]): SupportRequest[] {
  return newestUpdatedFirst(requests.filter((request) => request.status === "open"));
}

export function resolvedSupportRequests(requests: SupportRequest[]): SupportRequest[] {
  return newestUpdatedFirst(requests.filter((request) => request.status === "resolved"));
}

export function pendingSupportGrants(grants: SupportAccessGrant[]): SupportAccessGrant[] {
  return newestUpdatedFirst(grants.filter((grant) => grant.status === "requested"));
}

export function activeSupportGrants(
  grants: SupportAccessGrant[],
  now: Date = new Date(),
): SupportAccessGrant[] {
  return newestUpdatedFirst(grants.filter(
    (grant) => grant.status === "approved"
      && grant.expiresAt !== null
      && grant.expiresAt.getTime() > now.getTime(),
  ));
}

export function supportRequestStatusLabel(status: SupportRequestStatus): string {
  return status === "open" ? "Open" : "Resolved";
}

export function supportAccessGrantStatusLabel(status: SupportAccessGrantStatus): string {
  switch (status) {
    case "requested":
      return "Awaiting Approval";
    case "approved":
      return "Active";
    case "declined":
      return "Declined";
    case "revoked":
      return "Revoked";
    case "expired":
      return "Expired";
  }
}
